/**
 * Manual time show edit helper functions
 * Handles duration input changes for both 12-hour and 24-hour formats
 */

import { processManualTime } from "./manualTimeCalculation";
import { convertTo12HourFormat, convertTo24HourFormat, convertTo24HourFormatback } from "../services/timeFormat";
import { validateTimeFormat } from "../services/trackerServices";
import { covertTimeSatmp } from "../services/trackerServices";
import { handleError } from "./errorHandling";

/**
 * Creates manual time show edit handlers
 * @param {Object} dependencies - Object containing all necessary dependencies
 */
export const createManualTimeShowEditHandlers = ({
  // State values
  startTimeManual,
  startTimeManual24,
  date,
  // Setters
  setUFormatedTimeManual,
  setUManualFormatedTimeError,
  setUManualStartTimeError,
  setUManualEndTimeError,
  setStartTimeManual,
  setEndTimeManual,
  setEndTimeManual24,
  setSplittedDate,
  setSplitted,
  setUDuration,
}) => {
  /**
   * Handle duration input change for 12-hour format
   * @param {string} timeSec - Duration string in format "HH:mm:ss"
   */
  const manualTimeShowEdit = (timeSec) => {
    try {
      // If input is empty, just set it and return
      if (!timeSec || !timeSec.trim()) {
        setUFormatedTimeManual(timeSec || "");
        setUManualFormatedTimeError(false);
        return;
      }

      // Use 12-hour format start time, with fallback to converted 24-hour format if needed
      let startTimeForCalculation = startTimeManual && startTimeManual.trim()
        ? startTimeManual.trim()
        : null;

      // If startTimeManual is not available, try to use startTimeManual24 converted to 12-hour
      if (!startTimeForCalculation && startTimeManual24 && startTimeManual24.trim()) {
        const converted12Hour = convertTo12HourFormat(startTimeManual24.trim());
        if (converted12Hour) {
          startTimeForCalculation = converted12Hour;
          // Also update startTimeManual state to ensure validation works correctly
          setStartTimeManual(converted12Hour);
        }
      }

      // Check if input is clearly invalid (contains non-numeric characters except colons)
      const trimmedTime = timeSec.trim();
      const hasInvalidChars = trimmedTime && /[^0-9:]/.test(trimmedTime);
      const isPartialInput = trimmedTime && trimmedTime.length > 0 && trimmedTime.length < 5;

      // If input has invalid characters and is not just partial, show error immediately
      if (hasInvalidChars && !isPartialInput) {
        setUManualFormatedTimeError(true);
        setUFormatedTimeManual(timeSec);
        return;
      }

      // Use utility function to process manual time
      const result = processManualTime({
        timeSec,
        date,
        startTimeManual: startTimeForCalculation,
      });

      // Handle errors - but be less aggressive for partial input during typing
      if (!result.success) {
        // Only show error for clearly invalid input or missing start time, not for partial input
        if (!isPartialInput || result.error === "INVALID_START_TIME") {
          handleError(
            new Error(result.message),
            "manual time calculation",
            { errorType: result.error, timeSec }
          );
        }
        setUManualFormatedTimeError(true);
        setUFormatedTimeManual(timeSec);
        return;
      }
      // Clear errors first to prevent any validation from setting them back
      setUManualStartTimeError(false);
      setUManualEndTimeError(false);
      setUManualFormatedTimeError(false);

      // Handle split time (time spanning multiple days)
      if (result.splitted) {
        setEndTimeManual(result.endTime);
        // Also update 24-hour format end time to keep both in sync
        const endTime24 = convertTo24HourFormat(result.endTime);
        if (endTime24) {
          setEndTimeManual24(endTime24);
        }
        setSplittedDate(result.splittedDate);
        setSplitted(true);
      } else {
        // Handle single day time
        setUDuration(result.totalSeconds);
        setEndTimeManual(result.endTime);
        // Also update 24-hour format end time to keep both in sync
        const endTime24 = convertTo24HourFormat(result.endTime);
        if (endTime24) {
          setEndTimeManual24(endTime24);
        }
        setSplittedDate([]);
        setSplitted(false);
      }

      setUFormatedTimeManual(timeSec);

      // Clear errors again after state updates to ensure they stay false
      // This handles any validation that might run after state updates
      setTimeout(() => {
        setUManualStartTimeError(false);
        setUManualEndTimeError(false);
        setUManualFormatedTimeError(false);
      }, 0);
    } catch (error) {
      handleError(error, "manual time calculation", { timeSec });
      setUManualFormatedTimeError(true);
      setUFormatedTimeManual(timeSec);
    }
  };

  /**
   * Handle duration input change for 24-hour format
   * @param {string} timeSec - Duration string in format "HH:mm:ss"
   */
  const manualTimeShowEdit24 = (timeSec) => {
    try {
      // If input is empty, just set it and return
      if (!timeSec || !timeSec.trim()) {
        setUFormatedTimeManual(timeSec || "");
        setUManualFormatedTimeError(false);
        return;
      }

      // Use 12-hour format start time, with fallback to converted 24-hour format if needed
      let startTimeForCalculation = startTimeManual && startTimeManual.trim()
        ? startTimeManual.trim()
        : null;

      // If startTimeManual is not available, try to use startTimeManual24 converted to 12-hour
      if (!startTimeForCalculation && startTimeManual24 && startTimeManual24.trim()) {
        const converted12Hour = convertTo12HourFormat(startTimeManual24.trim());
        if (converted12Hour) {
          startTimeForCalculation = converted12Hour;
          // Also update startTimeManual state to ensure validation works correctly
          setStartTimeManual(converted12Hour);
        }
      }

      // Check if input is clearly invalid (contains non-numeric characters except colons)
      const trimmedTime = timeSec.trim();
      const hasInvalidChars = trimmedTime && /[^0-9:]/.test(trimmedTime);
      const isPartialInput = trimmedTime && trimmedTime.length > 0 && trimmedTime.length < 5;

      // If input has invalid characters and is not just partial, show error immediately
      if (hasInvalidChars && !isPartialInput) {
        setUManualFormatedTimeError(true);
        setUFormatedTimeManual(timeSec);
        return;
      }

      // Validate time format first
      if (!validateTimeFormat(timeSec)) {
        if (!isPartialInput) {
          setUManualFormatedTimeError(true);
        }
        setUFormatedTimeManual(timeSec);
        return;
      }

      // Parse time string
      const timeString = timeSec;
      const [hours, minutes, seconds] = timeString.split(":").map(Number);

      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
        console.error("Invalid timeSec format:", timeSec);
        setUManualFormatedTimeError(true);
        setUFormatedTimeManual(timeSec);
        return;
      }

      const totalSeconds = hours * 3600 + minutes * 60 + seconds;

      // Check if start time is available
      if (!startTimeForCalculation) {
        if (!isPartialInput) {
          handleError(
            new Error("Start time is required"),
            "manual time calculation",
            { errorType: "INVALID_START_TIME", timeSec }
          );
        }
        setUManualFormatedTimeError(true);
        setUFormatedTimeManual(timeSec);
        return;
      }

      // Use utility function to check for split time (time spanning multiple days)
      const result = processManualTime({
        timeSec,
        date,
        startTimeManual: startTimeForCalculation,
      });

      // Handle errors - but be less aggressive for partial input during typing
      if (!result.success) {
        // Only show error for clearly invalid input or missing start time, not for partial input
        if (!isPartialInput || result.error === "INVALID_START_TIME") {
          handleError(
            new Error(result.message),
            "manual time calculation",
            { errorType: result.error, timeSec }
          );
        }
        setUManualFormatedTimeError(true);
        setUFormatedTimeManual(timeSec);
        return;
      }

      // Clear errors first to prevent any validation from setting them back
      setUManualStartTimeError(false);
      setUManualEndTimeError(false);
      setUManualFormatedTimeError(false);

      // Handle split time (time spanning multiple days)
      if (result.splitted) {
        setEndTimeManual(result.endTime);
        setSplittedDate(result.splittedDate);
        setSplitted(true);

        // Extract end time from splittedDate.day2.stop and format directly in 24-hour format
        if (result.splittedDate && result.splittedDate.day2 && result.splittedDate.day2.stop) {
          const stopDate = new Date(result.splittedDate.day2.stop);
          if (!isNaN(stopDate.getTime())) {
            // Format directly in 24-hour format (HH:mm)
            const hours24 = stopDate.getHours().toString().padStart(2, "0");
            const minutes24 = stopDate.getMinutes().toString().padStart(2, "0");
            const endTime24 = `${hours24}:${minutes24}`;
            setEndTimeManual24(endTime24);
          } else {
            // Fallback: try to convert from 12-hour format
            const endTime24 = convertTo24HourFormat(result.endTime);
            if (endTime24) {
              setEndTimeManual24(endTime24);
            }
          }
        } else {
          // Fallback: try to convert from 12-hour format
          const endTime24 = convertTo24HourFormat(result.endTime);
          if (endTime24) {
            setEndTimeManual24(endTime24);
          }
        }
      } else {
        // Handle single day time - calculate end time directly in 24-hour format
        setUDuration(totalSeconds);

        const formattedDates = covertTimeSatmp(
          date,
          startTimeForCalculation
        );

        if (!formattedDates) {
          console.error("Invalid formattedDates from covertTimeSatmp");
          setUManualFormatedTimeError(true);
          setUFormatedTimeManual(timeSec);
          return;
        }

        const timestampInSeconds = Math.floor(new Date(formattedDates).getTime() / 1000);

        if (isNaN(timestampInSeconds)) {
          console.error("Invalid timestamp from formattedDates:", formattedDates);
          setUManualFormatedTimeError(true);
          setUFormatedTimeManual(timeSec);
          return;
        }

        const upcomingTimeInSeconds = timestampInSeconds + totalSeconds;
        const upcomingTime = new Date(upcomingTimeInSeconds * 1000);

        if (isNaN(upcomingTime.getTime())) {
          console.error("Invalid upcoming time generated:", upcomingTimeInSeconds);
          setUManualFormatedTimeError(true);
          setUFormatedTimeManual(timeSec);
          return;
        }

        // Format time in 12-hour format first (for 12-hour end time)
        const formattedTime12Hour = upcomingTime.toLocaleTimeString(["en-US"], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        if (!formattedTime12Hour) {
          console.error("Failed to format upcoming time:", upcomingTime);
          setUManualFormatedTimeError(true);
          setUFormatedTimeManual(timeSec);
          return;
        }

        // Convert to 24-hour format for endTimeManual24
        const convertedTime24 = convertTo24HourFormatback(formattedTime12Hour);

        if (!convertedTime24) {
          console.error("convertTo24HourFormatback failed for:", formattedTime12Hour);
          setUManualFormatedTimeError(true);
          setUFormatedTimeManual(timeSec);
          return;
        }

        // Set both formats
        setEndTimeManual24(convertedTime24);
        setEndTimeManual(formattedTime12Hour);
        setSplittedDate([]);
        setSplitted(false);
      }

      setUFormatedTimeManual(timeSec);

      // Clear errors again after state updates to ensure they stay false
      // This handles any validation that might run after state updates
      setTimeout(() => {
        setUManualStartTimeError(false);
        setUManualEndTimeError(false);
        setUManualFormatedTimeError(false);
      }, 0);
    } catch (error) {
      handleError(error, "manual time calculation", { timeSec });
      setUManualFormatedTimeError(true);
      setUFormatedTimeManual(timeSec);
    }
  };

  return {
    manualTimeShowEdit,
    manualTimeShowEdit24,
  };
};
